/**
 * The player: a capsule driven by Rapier's kinematic character controller, with the
 * camera riding at eye height.
 *
 * Kinematic rather than a dynamic rigid body, deliberately. A dynamic body player tips
 * over, slides on slopes, bounces off walls, accumulates angular velocity and catches on
 * thresholds. The kinematic controller treats wall sliding, step-up and step-down as
 * solved problems and makes movement a direct function of input — which is also what
 * makes it predictable enough to reason about.
 *
 * Everything per-frame here goes through refs and direct mutation. No state, no store
 * writes: this runs sixty times a second.
 */

import { CapsuleCollider, RigidBody, useRapier, type RapierRigidBody } from '@react-three/rapier';
import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import { inputState } from '../input/inputState';
import {
  ATTACH_PUSH,
  climbDirection,
  climbStep,
  ladderAnchor,
  ladderAt,
  ladderCrest,
} from '../game/climbing';
import type { Ladder } from '../game/maze/placeLadders';
import {
  isOutOfBounds,
  nearestCellCentre,
  RECOVERY_FEET_CLEARANCE,
  type OutOfBounds,
} from '../game/bounds';
import { isPlayable } from '../game/phase';
import { MAX_STEP_HEIGHT } from '../game/profile/validate';
import type { Spawn } from '../game/profile/types';
import { useGameStore } from '../store/gameStore';
import { cuboidHalfExtents, getObjectBody } from './objectRegistry';
import { pushPlayerPosition, resetPlayerTrail } from './playerTrail';
import { usePlayerLook } from './usePlayerLook';
import {
  CAMERA_OFFSET_FROM_CENTER,
  CHARACTER_OFFSET,
  DRAG_SPEED,
  GRAVITY,
  JUMP_SPEED,
  MAX_SLOPE_CLIMB_ANGLE,
  MIN_SLOPE_SLIDE_ANGLE,
  PLAYER_CAPSULE_HALF_HEIGHT,
  PLAYER_CENTER_OFFSET,
  PLAYER_RADIUS,
  SNAP_TO_GROUND_DISTANCE,
  SPRINT_SPEED,
  WALK_SPEED,
} from './constants';

type Props = {
  spawn: Spawn;
  /** The ladders of an outdoor level, if it has any. Empty indoors. */
  ladders: readonly Ladder[];
  /**
   * The lawn outside the labyrinth, and the grid a player found on it is put back into.
   * Absent indoors and on any level with no out-of-bounds lawn to speak of.
   */
  bounds?: OutOfBounds;
};

/**
 * A single frame can be arbitrarily long — a backgrounded tab, a GC pause. Without a
 * clamp, the first frame after one of those moves the player metres in a single step,
 * straight through a wall.
 */
const MAX_FRAME_DELTA = 0.1;

/**
 * Slack left between the carried box and whatever stops it, so the two never rest exactly
 * touching — a zero gap makes the next frame's cast report a hit at distance nothing and
 * the player judder against the wall.
 */
const CARRY_SKIN = 0.05;

export function Player({ spawn, ladders, bounds }: Props) {
  const bodyRef = useRef<RapierRigidBody>(null);
  const camera = useThree((state) => state.camera);
  const { world, rapier } = useRapier();
  const look = usePlayerLook(spawn.yaw);

  const controllerRef = useRef<ReturnType<typeof world.createCharacterController> | null>(null);
  const verticalVelocity = useRef(0);
  /** The ladder the player currently has hold of, or null. */
  const climbingRef = useRef<Ladder | null>(null);
  /** The carried box's shape, reused for the movement casts below. */
  const carryShapeRef = useRef<InstanceType<typeof rapier.Cuboid> | null>(null);

  useEffect(() => {
    const controller = world.createCharacterController(CHARACTER_OFFSET);

    // Step onto low obstacles instead of being stopped dead by a threshold. The third
    // argument includes dynamic bodies, so the player can also step onto a pushed crate.
    controller.enableAutostep(MAX_STEP_HEIGHT, PLAYER_RADIUS * 0.5, true);
    // Stay glued to the floor walking down a step, rather than briefly falling.
    controller.enableSnapToGround(SNAP_TO_GROUND_DISTANCE);
    controller.setMaxSlopeClimbAngle(MAX_SLOPE_CLIMB_ANGLE);
    controller.setMinSlopeSlideAngle(MIN_SLOPE_SLIDE_ANGLE);
    // Slide along a wall hit at an angle instead of stopping — this is what makes
    // corridors feel navigable rather than sticky.
    controller.setSlideEnabled(true);
    controller.setApplyImpulsesToDynamicBodies(true);

    controllerRef.current = controller;

    return () => {
      controllerRef.current = null;
      world.removeCharacterController(controller);
    };
  }, [world]);

  // `Player` is keyed on `${level.id}:${arriveAt}` in App.tsx, so a mount is exactly a new
  // level or a new arrival — precisely when a trail from the previous level must not
  // survive into this one.
  useEffect(() => {
    resetPlayerTrail();
  }, []);

  /**
   * The ladder branch, run before anything else moves the player.
   *
   * Returns false when it has already placed the body for this frame — while attached the
   * position is written straight out rather than solved, because there is nothing to
   * solve: the anchor is derived from the ladder, not steered by the player, and it sits a
   * capsule clear of the hedge face with 2.4 m of corridor behind it. Returns true to let
   * the normal walk take the frame.
   */
  const climb = (
    body: RapierRigidBody,
    current: { x: number; y: number; z: number },
    delta: number,
    pushX: number,
    pushZ: number,
    pushLength: number,
  ): boolean => {
    let ladder = climbingRef.current;
    // A level swap leaves a stale ladder behind — it is not in the new level's list.
    if (ladder && !ladders.includes(ladder)) ladder = null;

    /** How squarely the player is pushing at a ladder: 1 straight at it, -1 straight off. */
    const into = (candidate: Ladder) =>
      pushLength === 0 ? 0 : (pushX * candidate.into.x + pushZ * candidate.into.z) / pushLength;

    if (!ladder && ladders.length > 0) {
      const candidate = ladderAt(current, ladders);
      // Taking hold is a deliberate push at it. Brushing past one must never grab.
      if (candidate && into(candidate) > ATTACH_PUSH) ladder = candidate;
    }

    if (!ladder) {
      climbingRef.current = null;
      return true;
    }

    // Letting go: gravity and the controller take the frame from here, exactly as they
    // would for a jump off any ledge.
    if (inputState.jump) {
      inputState.jump = false;
      verticalVelocity.current = JUMP_SPEED;
      climbingRef.current = null;
      return true;
    }

    const push = into(ladder);
    const feetY = climbStep(ladder, current.y - PLAYER_CENTER_OFFSET, push, delta);
    const direction = climbDirection(push);
    // Nothing falls while it is holding a ladder — including the gravity accumulated on
    // the way to one.
    verticalVelocity.current = 0;

    // Off at the bottom by backing away; the walk resumes this same frame.
    if (feetY <= 0 && direction < 0) {
      climbingRef.current = null;
      return true;
    }

    // Off at the top by pushing on. The step lands on the middle of the crest, which the
    // hedge's flat-topped collider holds up — the earned vantage, see the change's D5.
    const toppedOut = feetY >= ladder.height && direction > 0;
    const stand = toppedOut ? ladderCrest(ladder) : ladderAnchor(ladder, current);
    const centerY = (toppedOut ? ladder.height + CHARACTER_OFFSET : feetY) + PLAYER_CENTER_OFFSET;

    body.setNextKinematicTranslation({ x: stand.x, y: centerY, z: stand.z });
    camera.position.set(stand.x, centerY + CAMERA_OFFSET_FROM_CENTER, stand.z);
    // The rungs and the crest are exactly where the trail must record height from (D1) —
    // it is the only way the dog ever learns a ladder is there.
    pushPlayerPosition(stand.x, feetY, stand.z);
    climbingRef.current = toppedOut ? null : ladder;
    return false;
  };

  useFrame((_, rawDelta) => {
    const body = bodyRef.current;
    const controller = controllerRef.current;
    if (!body || !controller) return;

    // Read the phase imperatively — subscribing would re-render this component on every
    // pause, and the value is only needed inside the loop.
    if (!isPlayable(useGameStore.getState().phase)) return;

    const delta = Math.min(rawDelta, MAX_FRAME_DELTA);
    const collider = body.collider(0);
    const current = body.translation();
    const { yaw } = look.current;

    // Movement is relative to where the camera faces. three.js looks down -Z by default.
    const forwardX = -Math.sin(yaw);
    const forwardZ = -Math.cos(yaw);
    const rightX = Math.cos(yaw);
    const rightZ = -Math.sin(yaw);

    const { move } = inputState;
    // Kept unscaled as well as stepped: the ladders below ask which way the player is
    // pushing, and a distance-per-frame answers that differently at 30 fps and at 120.
    const pushX = forwardX * move.y + rightX * move.x;
    const pushZ = forwardZ * move.y + rightZ * move.x;
    const pushLength = Math.hypot(pushX, pushZ);
    // The whole of sprinting, on the ground: a longer step in the direction already being
    // asked for. The ladder branch below is deliberately untouched — a climb has its own,
    // slower pace, and running up a ladder is not a thing this game offers.
    // Dragging beats sprinting: a player towing a table moves at the table's pace whatever
    // else they ask for, and letting a sprint override it would drag the table faster than
    // the casts that keep it out of the hedges can resolve.
    const dragging = useGameStore.getState().draggedObjectId !== null;
    const speed = dragging ? DRAG_SPEED : inputState.sprint ? SPRINT_SPEED : WALK_SPEED;
    const step = speed * delta;
    const desired = { x: pushX * step, y: 0, z: pushZ * step };

    if (!climb(body, current, delta, pushX, pushZ, pushLength)) return;

    // Jump only from the ground (last frame's contact), consuming the press so a
    // held key does not turn every landing into an instant relaunch.
    if (inputState.jump && controller.computedGrounded()) {
      inputState.jump = false;
      verticalVelocity.current = JUMP_SPEED;
    }

    verticalVelocity.current += GRAVITY * delta;
    desired.y = verticalVelocity.current * delta;

    // EXCLUDE_SENSORS is load-bearing: doorways and drop zones are filled by sensor
    // colliders, and without the flag the controller treats them as walls — the
    // player cannot pass an open door. The carried box is solid but must not block
    // its own carrier, so it is skipped by id.
    const carriedObjectId = useGameStore.getState().carriedObjectId;

    // What you are holding stops you, the way a real box does. Without this the box
    // halts against a wall (its own controller keeps it out) and the player keeps
    // walking into it, ending up standing inside what they are carrying. Clamped per
    // axis rather than by a normal, so walking *along* a wall with a box still slides.
    const carried = carriedObjectId === null ? null : getObjectBody(carriedObjectId);
    if (carried) {
      const half = cuboidHalfExtents(carried);
      const shape = (carryShapeRef.current ??= new rapier.Cuboid(half.x, half.y, half.z));
      shape.halfExtents = half;
      const from = carried.translation();
      const rotation = carried.rotation();

      const clamped = (delta: number, axis: 'x' | 'z') => {
        if (Math.abs(delta) < 1e-5) return delta;
        const sign = Math.sign(delta);
        const hit = world.castShape(
          from,
          rotation,
          { x: axis === 'x' ? sign : 0, y: 0, z: axis === 'z' ? sign : 0 },
          shape,
          0,
          Math.abs(delta) + CARRY_SKIN,
          true,
          rapier.QueryFilterFlags.EXCLUDE_SENSORS,
          undefined,
          undefined,
          undefined,
          (candidate) => {
            const data = candidate.parent()?.userData as { kind?: string; id?: string } | undefined;
            if (data?.kind === 'player') return false;
            return !(data?.kind === 'carryable' && data.id === carriedObjectId);
          },
        );
        if (!hit) return delta;
        return sign * Math.min(Math.abs(delta), Math.max(0, hit.time_of_impact - CARRY_SKIN));
      };

      desired.x = clamped(desired.x, 'x');
      desired.z = clamped(desired.z, 'z');
    }
    controller.computeColliderMovement(
      collider,
      desired,
      rapier.QueryFilterFlags.EXCLUDE_SENSORS,
      undefined,
      (candidate) => {
        if (carriedObjectId === null) return true;
        const data = candidate.parent()?.userData as { kind?: string; id?: string } | undefined;
        return !(data?.kind === 'carryable' && data.id === carriedObjectId);
      },
    );
    const corrected = controller.computedMovement();

    if (controller.computedGrounded() && verticalVelocity.current <= 0) {
      // Otherwise gravity accumulates while standing still and the player accelerates
      // downward into the floor, which the solver then has to fight every frame. The
      // sign guard keeps the take-off frame — still reported grounded — from zeroing
      // a jump before it starts.
      verticalVelocity.current = 0;
    }

    const next = {
      x: current.x + corrected.x,
      y: current.y + corrected.y,
      z: current.z + corrected.z,
    };

    // The lawn outside the labyrinth is scenery, and a player who has stepped off the
    // outside of a crest onto it is put back inside (design D5). This overwrites `next`
    // rather than writing the body a second time: the kinematic controller solves from
    // where it last saw the capsule, and two writes in a frame leave it solving from the
    // wrong place. The question is asked of the feet, which is the convention the boxes
    // and `Spawn.position` both use.
    if (bounds && isOutOfBounds(bounds, next.x, next.y - PLAYER_CENTER_OFFSET, next.z)) {
      const cell = nearestCellCentre(bounds, next.x, next.z);
      next.x = cell.x;
      next.y = RECOVERY_FEET_CLEARANCE + PLAYER_CENTER_OFFSET;
      next.z = cell.z;
      // Nothing of the trip survives the landing: no accumulated fall to slam the capsule
      // into the floor it is set down over, no ladder still held on the far side of a
      // hedge, and no trail leading a follower out over the crest after them.
      verticalVelocity.current = 0;
      climbingRef.current = null;
      resetPlayerTrail();
    }

    body.setNextKinematicTranslation(next);
    camera.position.set(next.x, next.y + CAMERA_OFFSET_FROM_CENTER, next.z);
    // Feet, not the capsule centre — the same convention `Spawn.position` uses, and what
    // the dog's own height-from-the-trail (D1) assumes.
    pushPlayerPosition(next.x, next.y - PLAYER_CENTER_OFFSET, next.z);
  });

  return (
    <RigidBody
      ref={bodyRef}
      type="kinematicPosition"
      colliders={false}
      position={[spawn.position.x, spawn.position.y + PLAYER_CENTER_OFFSET, spawn.position.z]}
      enabledRotations={[false, false, false]}
      userData={{ kind: 'player' }}
    >
      <CapsuleCollider args={[PLAYER_CAPSULE_HALF_HEIGHT, PLAYER_RADIUS]} />
    </RigidBody>
  );
}
